import {
  AspectRatio,
  Badge,
  Button,
  Card,
  Group,
  List,
  Modal,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { useState } from "react";
import {
  EmptyState,
  ErrorState,
  FilterBar,
  LoadingState,
  PageHeader,
  useReturnFocus,
  useSearchParamState,
} from "@how/ui";
import {
  formatDuration,
  formatSermonDate,
  sermonAudioFileUrl,
  sermonVideoEmbed,
} from "@how/website-core";
import { env } from "../../lib/env";
import { RichTextView } from "../../lib/RichTextView";
import { usePortalSermons, type PortalSermon } from "./sermon-queries";

/**
 * Sermons (ADR 0061, the member's half per ADR 0037): the church's published sermons, newest first, by series and
 * speaker, as Church Center's sermon channel. A member watches the video, listens to the audio and follows the notes
 * without leaving the portal. Nothing here is personal: every member of the church sees the same list.
 */
export function SermonsPage() {
  const sermons = usePortalSermons();
  const [search, setSearch] = useSearchParamState("q");
  const [seriesFilter, setSeriesFilter] = useSearchParamState("series");
  const [speakerFilter, setSpeakerFilter] = useSearchParamState("speaker");
  const [open, setOpen] = useState<PortalSermon | null>(null);
  const focus = useReturnFocus();

  const all = sermons.data ?? [];
  const options = (values: (string | null | undefined)[]) =>
    [...new Set(values.filter((v): v is string => typeof v === "string"))]
      .sort()
      .map((v) => ({ value: v, label: v }));
  const needle = search.trim().toLowerCase();
  const listed = all.filter(
    (s) =>
      (seriesFilter === "" || s.series?.title === seriesFilter) &&
      (speakerFilter === "" || s.speaker?.name === speakerFilter) &&
      (needle === "" ||
        s.title.toLowerCase().includes(needle) ||
        s.scripture.some((r) => r.toLowerCase().includes(needle))),
  );

  return (
    <>
      <PageHeader
        title="Sermons"
        description="Watch, listen and follow the notes of past messages."
      />
      <FilterBar
        search={search}
        onSearchChange={(value) => {
          setSearch(value === "" ? undefined : value);
        }}
        searchPlaceholder="Search by title or scripture"
        filters={
          <>
            <Select
              aria-label="Series"
              placeholder="Every series"
              clearable
              data={options(all.map((s) => s.series?.title))}
              value={seriesFilter === "" ? null : seriesFilter}
              onChange={(value) => {
                setSeriesFilter(value ?? undefined);
              }}
            />
            <Select
              aria-label="Speaker"
              placeholder="Every speaker"
              clearable
              data={options(all.map((s) => s.speaker?.name))}
              value={speakerFilter === "" ? null : speakerFilter}
              onChange={(value) => {
                setSpeakerFilter(value ?? undefined);
              }}
            />
          </>
        }
      />
      {sermons.isPending ? (
        <LoadingState rows={4} />
      ) : sermons.isError ? (
        <ErrorState message={sermons.error.message} onRetry={() => void sermons.refetch()} />
      ) : all.length === 0 ? (
        <EmptyState
          title="No sermons yet"
          description="Your church's sermons appear here once they are published."
        />
      ) : listed.length === 0 ? (
        <EmptyState title="No sermons match" description="Try another series or speaker." />
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
          {listed.map((s) => (
            <Card key={s.id} withBorder radius="md" padding="lg" data-sermon={s.slug}>
              <Stack gap={6} h="100%">
                <Text component="h2" fw={600} m={0} size="lg">
                  {s.title}
                </Text>
                <Text size="sm" c="dimmed">
                  {[formatSermonDate(s.preachedOn), s.speaker?.name].filter(Boolean).join(" · ")}
                </Text>
                {s.series === null ? null : <Text size="sm">Series: {s.series.title}</Text>}
                {s.scripture.length === 0 ? null : <Text size="sm">{s.scripture.join("; ")}</Text>}
                {s.summary === null ? null : (
                  <Text size="sm" c="dimmed" lineClamp={3}>
                    {s.summary}
                  </Text>
                )}
                <Group gap={4} mt="auto">
                  {s.video === null ? null : (
                    <Badge variant="light" color="gray">
                      Video
                    </Badge>
                  )}
                  {s.audio === null ? null : (
                    <Badge variant="light" color="gray">
                      Audio
                    </Badge>
                  )}
                  {s.notes === null ? null : (
                    <Badge variant="light" color="gray">
                      Notes
                    </Badge>
                  )}
                </Group>
                <Group>
                  <Button
                    variant="light"
                    aria-label={`Open ${s.title}`}
                    onClick={() => {
                      focus.remember();
                      setOpen(s);
                    }}
                  >
                    Open
                  </Button>
                </Group>
              </Stack>
            </Card>
          ))}
        </SimpleGrid>
      )}
      {open === null ? null : (
        <SermonModal
          sermon={open}
          onClose={() => {
            setOpen(null);
            focus.restore();
          }}
        />
      )}
    </>
  );
}

function SermonModal({
  sermon,
  onClose,
}: {
  readonly sermon: PortalSermon;
  readonly onClose: () => void;
}) {
  const audio = sermon.audio;
  const audioSrc =
    audio === null
      ? null
      : "path" in audio
        ? sermonAudioFileUrl(env.VITE_SUPABASE_URL, audio.path)
        : audio.url;
  // Uploaded audio plays here; audio the church hosts elsewhere opens where it lives (the portal plays only from
  // HoW's own storage, infrastructure/docker/Caddyfile's media-src).
  const playsHere = audio !== null && "path" in audio;
  return (
    <Modal
      opened
      onClose={onClose}
      title={sermon.title}
      size="xl"
      closeButtonProps={{ "aria-label": "Close" }}
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          {[
            formatSermonDate(sermon.preachedOn),
            sermon.speaker === null
              ? null
              : sermon.speaker.role === null
                ? sermon.speaker.name
                : `${sermon.speaker.name}, ${sermon.speaker.role}`,
            sermon.series?.title,
          ]
            .filter(Boolean)
            .join(" · ")}
        </Text>
        {sermon.video === null ? null : (
          <AspectRatio ratio={16 / 9}>
            <iframe
              src={sermonVideoEmbed(sermon.video)}
              title={`Watch: ${sermon.title}`}
              loading="lazy"
              referrerPolicy="strict-origin-when-cross-origin"
              allow="encrypted-media; picture-in-picture; fullscreen"
              allowFullScreen
              style={{ border: 0, borderRadius: 8 }}
            />
          </AspectRatio>
        )}
        {audioSrc === null ? null : playsHere ? (
          <Stack gap={4}>
            <Text size="sm" fw={600}>
              Listen
              {audio.durationSeconds === null ? "" : ` · ${formatDuration(audio.durationSeconds)}`}
            </Text>
            <audio controls preload="none" src={audioSrc} style={{ width: "100%" }}>
              <a href={audioSrc}>Download the audio</a>
            </audio>
          </Stack>
        ) : (
          <Group>
            <Button
              component="a"
              href={audioSrc}
              target="_blank"
              rel="noreferrer"
              variant="default"
            >
              Listen (opens in a new tab)
            </Button>
          </Group>
        )}
        {sermon.scripture.length === 0 ? null : (
          <Text size="sm">
            <strong>Scripture:</strong> {sermon.scripture.join("; ")}
          </Text>
        )}
        {sermon.summary === null ? null : <Text size="sm">{sermon.summary}</Text>}
        {sermon.notes === null ? null : (
          <Stack gap="xs">
            <Title order={2} size="h4">
              Notes
            </Title>
            <RichTextView nodes={sermon.notes} />
          </Stack>
        )}
        {sermon.resources.length === 0 ? null : (
          <Stack gap="xs">
            <Title order={2} size="h4">
              Resources
            </Title>
            <List>
              {sermon.resources.map((link) => (
                <List.Item key={`${link.label}-${link.href}`}>
                  <a href={link.href} target="_blank" rel="noreferrer">
                    {link.label} (opens in a new tab)
                  </a>
                </List.Item>
              ))}
            </List>
          </Stack>
        )}
      </Stack>
    </Modal>
  );
}
